import { tokyoYear, toWareki } from "../datetime.ts";
import type { SensitivityRules } from "../review-due.ts";
import { COMMON_RULES } from "./common.ts";
import type { ProfessionPack } from "./types.ts";

const yearLabel = (y: number) => `${y}年（${toWareki(y)}）`;

/** What in a web-marketing article goes stale: prices, specifications, deadlines, dates, changes. */
const WEB_SENSITIVITY: SensitivityRules = {
  amount: { label: "料金・数値", pattern: /\d[\d,，.]*\s?(?:万円|円|%|％|件|倍)|単価|相場/ },
  relief: { label: "仕様・制度", pattern: /仕様|アルゴリズム|アップデート|補助金|助成金|制度|要件|対象(?:と|に)なる/ },
  deadline: { label: "期限・公募", pattern: /期限|締め切り|締切|公募|申請期間|月\d+日まで/ },
  year: { label: "時点の書き方", pattern: /令和\d+年|\d{4}年(?:時点|現在|\d+月)|最新版|バージョン/ },
  reform: { label: "変更・廃止の記述", pattern: /変更|廃止|終了|新機能|リリース|予定|見込み|移行/ },
};

/**
 * Web 制作・システム開発会社: the pack DHO's own site (dho.jp) writes
 * with. Not a 士業: no registration number, the 代表 gives the final check,
 * and the sources are Google's own documentation and the ministries'
 * pages on subsidies. The advertising rules are the general ones
 * (景品表示法) plus the promises this trade is prone to.
 */
export const WEB_AGENCY: ProfessionPack = {
  id: "web_agency",
  label: "Web 制作・システム開発会社",
  supervisorTitle: "代表",
  registrationLabel: "",
  reviewer: "監修者",
  writingRules: [
    "依頼主の Web 制作・システム開発会社の立場で書く。文体は「です・ます」。一人称と表記は会社の設定に従う。",
    "断定してよいのは、Google の公式の資料、官公庁や補助金の事務局の資料などの一次資料に根拠がある場合だけ。根拠とした資料を出典として示す。",
    "検索の仕組みや広告の仕様は、確認した時点を明記する（例：「2026年10月時点の Google 検索セントラルの説明では」）。変更の予定は「予定」と書く。",
    "料金・期間・成果の数字は、一次資料か自社の実績で確認できたものだけを書く。他社の事例を自社の実績のように書かない。",
    "検索順位・集客数・売上の保証（「必ず1位」「問い合わせが倍になる」など）、根拠のない最上級、他社との比較は書かない。",
    "読者の会社の規模や業種で答えが変わることは、場合分けして書く。個別の判断が必要な内容は「ご相談ください」とし、CTA につなげる。",
    "専門用語は初出で短く説明する。経営者が読んで、自社に当てはめられる書き方にする。",
    "他のサイトの文章を写さない。競合の情報は構成の参考と、足りない点の把握にだけ使う。",
    "検索した人の知りたいことに冒頭で答える。必要に応じて FAQ を入れる。",
    "文字数稼ぎの水増しをしない。",
  ],
  expressionRules: [
    ...COMMON_RULES,
    {
      id: "web-rank-guarantee",
      pattern: /(必ず|確実に|絶対に?)(上位|1位|一位|検索結果の上|集客|売上|問い合わせ)|(上位表示|1位|一位)(を)?保証/g,
      severity: "high",
      category: "成果の保証",
      message: "検索順位や集客の成果を保証する表現は、誇大・誤認を招く広告とみなされるおそれがあります",
      suggestion: "「〜が期待できます」とし、どのような条件で成果が変わるかを説明する",
    },
    {
      id: "web-google-insider",
      pattern: /Google (と|の)(提携|公認|認定パートナー)|Google 公式の?(代理店|パートナー)/g,
      severity: "medium",
      category: "提携・認定の表示",
      message: "Google との提携や認定を示す表現は、事実として証明できる場合だけ使えます",
      suggestion: "認定の正式な名称と確認できるページを示すか、表現を削る",
    },
    {
      id: "web-vague-stats",
      pattern: /(多くの|ほとんどの)(企業|会社|サイト)が.{0,20}(成功|売上が|伸び)/g,
      severity: "low",
      category: "根拠があいまい",
      message: "割合や件数の根拠がない一般化です",
      suggestion: "出典のある数字を示すか、自社の実績として件数と期間を書く",
    },
  ],
  trustedDomains: ["developers.google.com", "support.google.com", "search.google.com", "web.dev", "ads.google.com", "business.google.com", "digital.go.jp", "meti.go.jp", "it-shien.smrj.go.jp", "smrj.go.jp", "ipa.go.jp", "caa.go.jp", "soumu.go.jp", "mhlw.go.jp", "laws.e-gov.go.jp", "elaws.e-gov.go.jp"],
  authorityDomains: ["ferret-plus.com", "web-tan.forum.impressrd.jp", "seolaboratory.jp", "mieru-ca.com", "liskul.com", "hubspot.jp", "note.com", "nikkei.com", "ja.wikipedia.org"],
  specialtyOptions: ["ホームページ制作", "SEO 対策", "LP 制作", "Google 広告・SNS 広告", "業務システム開発", "AI を使った業務の自動化", "Excel・Access・FileMaker の Web 化", "IT 導入補助金", "採用サイト・求人", "MEO（Google ビジネスプロフィール）"],
  // 制作と集客の暦: budgets, subsidies and the seasons companies rethink their sites.
  seasons: [
    { id: "nendo-yosan", name: "新年度の予算と計画", start: { month: 1, day: 15 }, end: { month: 3, day: 31 }, leadWeeks: 8, terms: ["ホームページ 制作 費用", "リニューアル", "予算", "見積もり", "システム 開発 費用"], note: "来年度の予算を決める時期です。費用の目安や進め方を調べる経営者が増えます。" },
    { id: "it-hojokin", name: "IT 導入補助金の公募", start: { month: 3, day: 1 }, end: { month: 7, day: 31 }, leadWeeks: 8, terms: ["IT 導入補助金", "補助金", "助成金", "インボイス 対応"], note: "例年、春から夏にかけて公募が続きます。公募の開始前に公開しておくと、検索が増える時期に間に合います。" },
    { id: "shinnendo", name: "新年度の体制づくり", start: { month: 4, day: 1 }, end: { month: 5, day: 31 }, leadWeeks: 6, terms: ["業務 効率化", "人手不足", "DX", "自動化", "採用サイト"], note: "新しい体制で業務の見直しや採用を考える時期です。" },
    { id: "kessan-natsu", name: "夏の見直し（上半期の振り返り）", start: { month: 8, day: 1 }, end: { month: 9, day: 30 }, leadWeeks: 6, terms: ["アクセス 解析", "SEO", "集客", "広告 効果"], note: "上半期の数字を見て、集客の見直しを考える経営者が増えます。" },
    { id: "nenmatsu", name: "年末の整理と来年の準備", start: { month: 11, day: 1 }, end: { month: 12, day: 31 }, leadWeeks: 8, terms: ["ホームページ リニューアル", "来年", "計画", "保守", "セキュリティ"], note: "年内に片付けたい案件と、来年の計画を考える時期です。" },
  ],
  officeNoun: "Web 制作・システム開発の会社",
  officeExample: "山田ウェブ制作",
  affiliationExample: "大阪商工会議所",
  sourcesPhrase: "Google の公式の資料や官公庁の資料",
  sourceExamples: "Google 検索セントラル・デジタル庁・IT 導入補助金事務局",
  keyFigures: "料金・期間・数値",
  publishers: {
    "developers.google.com": "Google 検索セントラル",
    "support.google.com": "Google ヘルプ",
    "search.google.com": "Google Search Console",
    "web.dev": "web.dev（Google）",
    "ads.google.com": "Google 広告",
    "business.google.com": "Google ビジネスプロフィール",
    "digital.go.jp": "デジタル庁",
    "meti.go.jp": "経済産業省",
    "it-shien.smrj.go.jp": "IT 導入補助金事務局",
    "smrj.go.jp": "中小企業基盤整備機構",
    "ipa.go.jp": "情報処理推進機構（IPA）",
    "caa.go.jp": "消費者庁",
    "soumu.go.jp": "総務省",
    "mhlw.go.jp": "厚生労働省",
    "e-gov.go.jp": "e-Gov 法令検索",
  },
  period: {
    label: yearLabel,
    short: (y) => `${y}年`,
    rules: (y) => `${yearLabel(y)}時点の情報`,
    brief: (y) => `基準とする時点：${yearLabel(y)}時点の情報（Google の仕様や補助金の制度は確認した時点を明記し、変更の予定は「予定」と書く）`,
    mustState: false,
    current: tokyoYear,
  },
  review: { name: "情報の見直し", when: "Google の仕様変更や、補助金の公募の替わりのあと", nowLabel: "現在", currentArticle: "最新の情報に合った記事", updateLabel: "情報の更新", sensitivity: WEB_SENSITIVITY },
  features: { articles: true },
};
